import { Prospect, BuyerPersona, EngagementMetrics, EmailTemplate } from '../../types/savvydata.ts';

// Generate a unique ID
const generateId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Mock data for Salesforce contacts
const salesforceContacts = [
  {
    id: 'contact1',
    FirstName: 'Nick',
    LastName: 'Baguley',
    Company: 'Baguley LLC',
    Industry: 'Artificial Intelligence',
    Website: 'https://baguleyllc.com',
    LinkedIn__c: 'https://linkedin.com/in/nickbaguley',
    City: 'Salt Lake City, UT',
  }
];

// Generate a mock enriched prospect with accurate data
const generateEnrichedProspect = (formData: any): Prospect => {
  return {
    id: generateId(),
    name: formData.name,
    company_name: formData.company_name,
    industry: formData.industry,
    website: formData.website,
    linkedin_url: formData.linkedin_url,
    description: 'SaaS technology company specializing in AI-driven marketing solutions.',
    employee_count: 45,
    location: formData.location,
    revenue_range: '$5M - $10M',
    technologies: ['Python', 'Hugging Face', 'LLM', 'KAG', 'React', 'Node.js'],
    score: {
      relevance: 0.85,
      engagement_potential: 0.92,
      budget_fit: 0.78,
      total_score: 0.87
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    crm_id: 'SF12345'
  };
};

// Generate a mock buyer persona
const generateBuyerPersona = (prospectId: string): BuyerPersona => {
  return {
    personality: {
      openness: 0.8,
      conscientiousness: 0.75,
      extraversion: 0.4,
      agreeableness: 0.6,
      neuroticism: 0.8
    },
    decision_style: {
      analytical: 0.95,
      intuitive: 0.6,
      collaborative: 0.6,
      directive: 0.3
    },
    communication_preferences: [
      'Data-driven presentations',
      'Email for initial contact',
      'Video calls for demos',
      'Concise messaging'
    ],
    influence_factors: [
      'ROI metrics',
      'Technical specifications',
      'Case studies',
      'Integration capabilities'
    ],
    adoption_stage: 'Late Majority',
    budget_authority: 'Decision Maker',
    tech_savviness: 0.95,
    risk_tolerance: 0.8,
    preferred_content_types: ['White papers', 'Video demos', 'Interactive tools'],
    industry_expertise: 0.95,
    pain_points: [
      { text: 'Inefficient lead qualification process', intensity: 0.9 },
      { text: 'Poor visibility into sales pipeline', intensity: 0.7 },
      { text: 'Low conversion rates from marketing efforts', intensity: 0.8 }
    ],
    motivations: [
      { text: 'Increase sales efficiency', strength: 0.9 },
      { text: 'Improve customer targeting accuracy', strength: 0.85 },
      { text: 'Reduce cost per acquisition', strength: 0.7 }
    ],
    last_updated: new Date().toISOString(),
    confidence_score: 0.85
  };
};

// Generate mock engagement metrics
const generateEngagementMetrics = (prospectId: string): EngagementMetrics => {
  return {
    email_engagement: 0.78,
    content_engagement: 0.65,
    meeting_engagement: 0.82,
    crm_activity: 0.7,
    social_engagement: 0.3,
    overall_score: 0.75,
    velocity: 0.12,
    recency: 0.2,
    frequency: 0.6,
    depth: 0.8
  };
};

// Generate mock email template
const generateEmailTemplate = (prospectId: string, templateType: string): EmailTemplate => {
  return {
    subject: 'Enhancing Your Marketing Strategy with AI-Driven Insights',
    body: `Hi Nick,

I noticed that Baguley LLC has been making significant strides in the technology sector, particularly with your innovative approach to client solutions.

Based on my research, companies like yours are often looking to:
- Improve lead qualification processes
- Gain better visibility into the sales pipeline
- Increase conversion rates from marketing efforts

Our SavvyData platform specifically addresses these challenges through AI-driven prospect research and personalized engagement strategies.

Would you be open to a brief 15-minute call next week to discuss how we've helped similar technology companies increase their conversion rates by an average of 35%?

Looking forward to connecting,

AI Sales Assistant
SavvyData Marketing Platform`,
    personalization: {
      pain_points_addressed: [
        'Lead qualification inefficiency',
        'Sales pipeline visibility',
        'Marketing conversion rates'
      ],
      buyer_persona_alignment: {
        communication_style: 'Analytical',
        decision_factors: 'ROI and metrics-focused'
      },
      decision_maker_focus: {
        role: 'CTO',
        priorities: 'Technical implementation and integration'
      }
    }
  };
};

// Mock API functions
export const mockApi = {
  prospects: {
    createProspect: async (formData: any) => {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      return generateEnrichedProspect(formData);
    },
    
    enrichProspect: async (id: string) => {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      // This would normally enrich the prospect with additional data
      return generateEnrichedProspect({
        name: 'Nick Baguley',
        company_name: 'Baguley LLC',
        industry: 'Technology',
        website: 'https://baguleyllc.com',
        linkedin_url: 'https://linkedin.com/in/nickbaguley',
        location: 'Salt Lake City, UT'
      });
    }
  },
  
  crm: {
    getSalesforceContacts: async (query: any = {}) => {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simple search
      if (query.search) {
        const searchTerm = query.search.toLowerCase();
        return salesforceContacts.filter(contact => 
          `${contact.FirstName} ${contact.LastName}`.toLowerCase().includes(searchTerm) ||
          contact.Company.toLowerCase().includes(searchTerm)
        );
      }
      
      return salesforceContacts;
    }
  },
  
  analytics: {
    getPersonaAnalysis: async (prospectId: string) => {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      return generateBuyerPersona(prospectId);
    },
    
    getEngagementMetrics: async (prospectId: string) => {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      return generateEngagementMetrics(prospectId);
    }
  },
  
  email: {
    generateEmail: async (prospectId: string, templateType: string) => {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1800));
      return generateEmailTemplate(prospectId, templateType);
    },
    sendEmail: async (emailData: any) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      return {
        success: true,
        message: "Email sent successfully",
        email_id: "em_" + Math.random().toString(36).substr(2, 9)
      };
    }
  }
};

export default mockApi; 